/**
 * P1.2 Hit impulse — pure math.
 *
 * When a `hit` event lands, the chassis is briefly knocked along the
 * direction the shot came from. Three channels share one envelope so
 * the punch reads as a single beat:
 *
 *   {dx, dz} — XZ push along the hit normal (away from shooter).
 *   scaleK   — uniform scale multiplier <1 ("compression"); decays
 *              back to 1.
 *
 * Envelope is fast-attack / cosine-decay, same shape as `recoil` but
 * shorter so a hit reads punchier than a fire kick. Pure functions —
 * no Three deps so we unit-test the math cleanly.
 */

export const HIT_DURATION_MS = 110;
export const HIT_DISTANCE = 0.07;
/** Peak compression. 0.04 → chassis scales to 0.96 at impact. */
export const HIT_COMPRESSION = 0.04;
/** Fraction of the window spent in attack phase (linear 0 → 1). */
const ATTACK_FRACTION = 0.14;

export interface HitImpulseOffset {
  dx: number;
  dz: number;
  /** Scale multiplier applied to the chassis; 1 = no change. */
  scaleK: number;
}

const REST: HitImpulseOffset = { dx: 0, dz: 0, scaleK: 1 };

/**
 * Normalized envelope (0..1) for `elapsedMs` inside the hit window.
 * Linear attack to 1 over the first ATTACK_FRACTION, cosine ease back
 * to 0 over the remainder. Returns 0 outside the window.
 */
export function hitEnvelope(elapsedMs: number | null): number {
  if (elapsedMs === null || elapsedMs < 0 || elapsedMs >= HIT_DURATION_MS) return 0;
  const t = elapsedMs / HIT_DURATION_MS;
  if (t < ATTACK_FRACTION) return t / ATTACK_FRACTION;
  const u = (t - ATTACK_FRACTION) / (1 - ATTACK_FRACTION);
  return 0.5 * (1 + Math.cos(u * Math.PI));
}

/**
 * Hit-impulse offset for a chassis whose most-recent `hitPulse`
 * started `elapsedMs` ago, struck along the unit-vector `(nx, nz)`.
 * `(nx, nz)` is the **incoming** direction (shooter → victim); the
 * push is applied along this vector so the chassis is knocked
 * *away from the shooter*.
 *
 * Caller should pre-normalize `(nx, nz)`; this util does not, so a
 * zero vector yields zero push (a fine fallback when the shooter
 * position is unknown — the scale pulse still fires).
 */
export function hitImpulse(
  elapsedMs: number | null,
  nx: number,
  nz: number,
): HitImpulseOffset {
  const env = hitEnvelope(elapsedMs);
  if (env === 0) return REST;
  const magnitude = HIT_DISTANCE * env;
  return {
    dx: nx * magnitude,
    dz: nz * magnitude,
    scaleK: 1 - HIT_COMPRESSION * env,
  };
}
